/*
  Series card close-up reveal - Free Flowing, Dog Days of Summer, Wisdom Nuggets.

  Loaded by snippets/series-card-assets.liquid. That snippet is rendered once
  per grid and each series page renders its grid TWICE (the drop engine emits
  the early-access and the public state and hides one), so this file is fetched
  once and EXECUTED TWICE. Every guard below is per element for that reason -
  a single global "already ran" flag would leave the second grid's cards
  unobserved if the hidden one happened to bind first.

  ===========================================================================
  TIMING IS DELIBERATELY IDENTICAL TO sections/ps-grid.liquid
  ===========================================================================
  The Print Shop grid and the series grids show the same card: the square
  print_shop.tile with the print_shop.detail close-up over it. A reader moving
  between those pages should not meet two different rhythms, so if you change a
  number here, change it there too.

  What this replaces, and why: the first version toggled the class straight off
  entry.isIntersecting inside a `rootMargin: -45% 0px -45% 0px` band. On a
  375x812 phone that band is 81px tall and the cards are 335px squares, one per
  row below 600px. A card was therefore revealed the whole time its top sat
  between y=30 and y=447, which is exactly the settled, readable position - the
  print itself was only on screen while the card was arriving at the bottom or
  leaving off the top. The band is also far taller than the row gap, so two
  neighbours could be mid-fade at once.

  The replacement, every part load-bearing:
    - No rootMargin. Gate on intersectionRatio, NOT isIntersecting: a threshold
      array only controls when the callback FIRES, and isIntersecting is true at
      any ratio above zero, so the first callback would arm a card barely on
      screen.
    - DWELL before revealing, so the PRINT is what you see first. Cancelled if
      the card stops qualifying, which is what stops a fast scroll strobing
      every card it passes.
    - HOLD, then back to the print. The print is the resting state of every
      card. Without this the reveal is one way for the life of the page, and
      since many detail crops are abstract paint texture rather than the figure,
      a scrolled page stops being a grid of recognisable paintings.
    - TWO GATES: reveal at 0.74, re-arm at 0.35. That gap is hysteresis - a card
      parked near either edge cannot oscillate, because leaving the revealed
      state takes a much bigger move than entering it. Do not collapse them.
    - scPeeked blocks an immediate re-reveal while the reader is still on the
      card; only the OUT gate clears it.

  Cards in the hidden grid have no box, so they never reach any ratio and never
  reveal. Nothing extra is needed to exclude them.
*/
(function () {
  var DWELL = 1600;
  var HOLD = 3000;
  var IN_RATIO = 0.74;
  var OUT_RATIO = 0.35;

  var io = null;

  function clearTimers(el) {
    if (el.scRevealTimer) { window.clearTimeout(el.scRevealTimer); el.scRevealTimer = null; }
    if (el.scHoldTimer) { window.clearTimeout(el.scHoldTimer); el.scHoldTimer = null; }
  }

  function onEntries(entries) {
    entries.forEach(function (entry) {
      var el = entry.target;
      var ratio = entry.intersectionRatio;

      if (ratio >= IN_RATIO) {
        if (el.scPeeked || el.scRevealTimer) return;
        el.scRevealTimer = window.setTimeout(function () {
          el.scRevealTimer = null;
          el.classList.add('is-inview');
          /* HOLD <= 0 is the escape hatch that matches reveal_hold: 0 on the
             Print Shop grid: reveal once and leave it. */
          if (HOLD <= 0) { el.scPeeked = true; io.unobserve(el); return; }
          el.scHoldTimer = window.setTimeout(function () {
            el.scHoldTimer = null;
            el.scPeeked = true;
            el.classList.remove('is-inview');
          }, HOLD);
        }, DWELL);
        return;
      }

      if (ratio < OUT_RATIO) {
        /* Gone, or nearly. Back to the print and re-arm, so scrolling up the
           grid shows paintings rather than paint texture. */
        clearTimers(el);
        el.scPeeked = false;
        el.classList.remove('is-inview');
        return;
      }

      /* Between the gates: kill a dwell that has not fired yet. A reveal
         already on screen is left alone to finish its hold. */
      if (el.scRevealTimer) { window.clearTimeout(el.scRevealTimer); el.scRevealTimer = null; }
    });
  }

  function observe(scope) {
    if (!window.matchMedia('(hover: none)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;

    var cards = (scope || document).querySelectorAll('.sc-media--hover');
    if (!cards.length) return;

    if (!io) {
      io = new IntersectionObserver(onEntries, { threshold: [0, OUT_RATIO, 0.36, IN_RATIO, 0.75] });
    }

    Array.prototype.forEach.call(cards, function (card) {
      if (card.getAttribute('data-sc-bound') === '1') return;
      card.setAttribute('data-sc-bound', '1');
      io.observe(card);
    });
  }

  /* The close-ups sit at opacity 0, so the browser defers their lazy load
     indefinitely and the first reveal would fade to an empty box, then pop in
     once the file arrived. Flipping them to eager AFTER load fetches them
     during idle time, so the reveal is instant and nothing competes with the
     initial render. */
  function warm() {
    var imgs = document.querySelectorAll('.sc-media__img--alt[loading="lazy"]');
    for (var i = 0; i < imgs.length; i++) { imgs[i].loading = 'eager'; }
  }

  function schedule() {
    if (window.requestIdleCallback) { window.requestIdleCallback(warm, { timeout: 2000 }); }
    else { window.setTimeout(warm, 400); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observe(); });
  } else {
    observe();
  }

  if (document.readyState === 'complete') { schedule(); }
  else { window.addEventListener('load', schedule, { once: true }); }

  /* Theme editor: a section reload swaps in fresh card markup, which carries no
     data-sc-bound and so is picked up here. */
  document.addEventListener('shopify:section:load', function (e) { observe(e.target); schedule(); });
})();